import { useCallback, useEffect, useRef, useState } from 'react';

// These handle the "window" checks in one place
var safelyGetValueFromLocalStorage = function safelyGetValueFromLocalStorage(key) {
  return typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null;
};

var safelySetValueToLocalStorage = function safelySetValueToLocalStorage(key, value) {
  return typeof localStorage !== 'undefined' ? localStorage.setItem(key, value) : undefined;
};

var safelyRemoveFromLocalStorage = function safelyRemoveFromLocalStorage(key) {
  return typeof localStorage !== 'undefined' ? localStorage.removeItem(key) : undefined;
};

var useIsMounted = function useIsMounted() {
  var isMounted = useRef(true);
  useEffect(function () {
    return function () {
      isMounted.current = false;
    };
  }, []);
  return isMounted;
};

/**
 * Wraps thr browsers localStorage in an interface similar to `useState`. I safe to import into components that undergo SSR/SSG (calls to localStorage are behind null checks).
 * @param key the localStorage key to read/write values to/from
 * @param transforms an optional serializer/deserialzer that values pass through on their way to/from localStorage. If this is not provided, all values will be `string | null`
 */
var useLocalStorage = function useLocalStorage(key, transforms) {
  var isMounted = useIsMounted();
  var rawDefaultValue = safelyGetValueFromLocalStorage(key);
  var deserializedDefaultValue = transforms ? transforms.deserialize(rawDefaultValue) : rawDefaultValue;

  var _useState = useState(deserializedDefaultValue),
      value = _useState[0],
      setValue = _useState[1];

  var setValueWithTransforms = useCallback(function (rawValue) {
    var serializedValue = transforms ? transforms.serialize(rawValue) : rawValue;

    if (serializedValue === null) {
      safelyRemoveFromLocalStorage(key); // only call set value if component is still mounted

      if (isMounted.current) {
        // explicitly call with null here in case the rawValue wasn't null
        // letting the consumer know about a possible issue during serialization
        // (it does mean there is some duplication but 🤷‍♂️)
        setValue(null);
      }
    } else {
      safelySetValueToLocalStorage(key, serializedValue);

      if (isMounted.current) {
        setValue(rawValue);
      }
    }
  }, [key, !!transforms]);
  return [value, setValueWithTransforms];
};

export default useLocalStorage;